import React from 'react';
import {
  Box,
  IconButton,
  useTheme,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsBackupRestoreOutlinedIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import { layoutGap } from '../../../../theme/theme';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { quickFillActionTooltips } from '../../../../shared/config';
import { createActionHelp, createDisabledHelp } from '../../../../shared/ui/common/helpModel';
import { OperationProgressIndicator } from '../../../../shared/ui/progress';
import {
  getWorkspaceIconButtonSx,
} from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';

export interface QuickFillUtilityChromeProps {
  pageGuideRef?: React.Ref<HTMLSpanElement>;
  recoveryControlsRef?: React.Ref<HTMLDivElement>;
  quickFillUndoTooltip: string;
  hasCompatibleQuickFillSessionUndo: boolean;
  quickFillSessionUndoDisabled: boolean;
  isUndoingQuickFillSessions: boolean;
  onUndoQuickFillSessions: () => void;
  hasUndoableParameterSetup: boolean;
  connectorUndoTooltip: string;
  isUndoingParameterSetup: boolean;
  onUndoParameterSetup: () => void;
  onOpenGuide: () => void;
}

export const QuickFillUtilityChrome: React.FC<QuickFillUtilityChromeProps> = ({
  pageGuideRef,
  recoveryControlsRef,
  quickFillUndoTooltip,
  hasCompatibleQuickFillSessionUndo,
  quickFillSessionUndoDisabled,
  isUndoingQuickFillSessions,
  onUndoQuickFillSessions,
  hasUndoableParameterSetup,
  connectorUndoTooltip,
  isUndoingParameterSetup,
  onUndoParameterSetup,
  onOpenGuide,
}) => {
  const theme = useTheme();
  const utilityButtonSx: Record<string, unknown> = {
    ...getWorkspaceIconButtonSx(theme, { size: 24 }),
    p: 0.25,
    flex: '0 0 auto',
  };
  const quickFillUndoHelp = hasCompatibleQuickFillSessionUndo
    ? createActionHelp('Undo last Quick Fill session', quickFillUndoTooltip, {
      downstream: 'Restores the most recent compatible Quick Fill session snapshot without touching unrelated workflow state.',
      note: 'Use this before saving again if a cleanup pass changed the wrong rows.',
    })
    : createDisabledHelp('Quick Fill undo unavailable', quickFillUndoTooltip, {
      blockedReason: quickFillUndoTooltip,
      nextStep: 'Create a compatible Quick Fill edit session before using undo.',
    });
  const connectorSetupUndoHelp = createActionHelp(
    'Revert connector setup',
    connectorUndoTooltip || 'Revert the last connector parameter setup action.',
    {
      downstream: 'Returns connector parameter setup to its previous state so you can retry the Quick Fill handoff cleanly.',
      note: 'This affects connector setup, not ordinary Quick Fill cell edits.',
    }
  );

  return (
    <Box
      role="group"
      aria-label="Quick Fill utility actions"
      data-testid="quickfill-utility-actions"
      sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.micro, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}
    >
      <Box
        ref={recoveryControlsRef}
        data-testid="quickfill-tour-target-recovery-controls"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: layoutGap.micro, flexWrap: 'wrap', minWidth: 0 }}
      >
        <ActionHelpTooltip
          help={quickFillUndoHelp}
          disabledHelp={quickFillUndoHelp}
          disabled={quickFillSessionUndoDisabled}
          placement="bottom"
        >
          <IconButton
            size="small"
            color={hasCompatibleQuickFillSessionUndo ? 'primary' : 'default'}
            onClick={onUndoQuickFillSessions}
            disabled={quickFillSessionUndoDisabled}
            aria-label="Undo last Quick Fill session"
            data-testid="quickfill-session-undo-button"
            sx={{
              ...utilityButtonSx,
              ...(hasCompatibleQuickFillSessionUndo ? { color: theme.palette.primary.main } : {}),
            }}
          >
            {isUndoingQuickFillSessions ? (
              <OperationProgressIndicator size={14} ariaLabel="Undoing Quick Fill sessions" />
            ) : (
              <UndoIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </ActionHelpTooltip>
        {hasUndoableParameterSetup ? (
          <ActionHelpTooltip help={connectorSetupUndoHelp} placement="bottom">
            <IconButton
              size="small"
              onClick={onUndoParameterSetup}
              disabled={isUndoingParameterSetup}
              aria-label="Revert connector parameter setup"
              sx={{
                ...utilityButtonSx,
                '&:hover': {
                  ...(utilityButtonSx['&:hover'] as object),
                  color: theme.palette.warning.main,
                },
              }}
            >
              <SettingsBackupRestoreOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </ActionHelpTooltip>
        ) : null}
      </Box>
      <ActionHelpTooltip help={quickFillActionTooltips.pageGuide} placement="bottom">
        <Box
          ref={pageGuideRef}
          component="span"
          data-testid="quickfill-tour-target-page-guide"
          sx={{
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            size="small"
            onClick={onOpenGuide}
            aria-label="Open Quick Fill page guide"
            sx={{
              ...utilityButtonSx,
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </ActionHelpTooltip>
    </Box>
  );
};
